import { useQueryClient } from '@tanstack/react-query';
import { useApiMutation, useApiQuery } from '@/hooks/use-api-query';
import { qk } from '@/lib/query/keys';
import type { SandboxSession } from '@/pages/sandbox';

export interface BindCodeResponse {
  code: string;
  issuedAt: string;
  consumedSessionId?: string | null;
}

// GET /sandbox/bind-code — workspace-scoped (X-Workspace-Id). Shared by
// SandboxPage and QuickStartPage so both surfaces read one cache entry per
// workspace. Gated on sessionScoped: firing before the cookie session is
// scoped to the active workspace's org 403s against WorkspaceGuard (see
// pages/members/queries.ts for the same pattern).
export function useBindCode(wsId: string | undefined, sessionScoped: boolean) {
  return useApiQuery<BindCodeResponse>(
    qk.workspace(wsId ?? '', 'sandbox', 'bind-code'),
    '/sandbox/bind-code',
    { enabled: !!wsId && sessionScoped },
  );
}

// DELETE /sandbox/sessions/:id. The sessions list itself rides the SSE
// stream (not query-cached), but the bind code can be re-issued once the
// session that consumed it is gone — invalidate so the code/QR stay current.
export function useDeleteSandboxSession(wsId: string | undefined) {
  const qc = useQueryClient();
  return useApiMutation<{ success?: boolean }, { sessionId: string }>(
    ({ sessionId }) => ({
      path: `/sandbox/sessions/${sessionId}`,
      init: { method: 'DELETE' },
      fallback: 'Failed to delete session',
    }),
    {
      onSuccess: () =>
        qc.invalidateQueries({
          queryKey: qk.workspace(wsId ?? '', 'sandbox', 'bind-code'),
        }),
    },
  );
}

// POST /sandbox/sessions/:id/reset-webhook — returns the updated session.
// Nothing to invalidate: the authoritative session patch arrives via SSE.
export function useResetSessionWebhook() {
  return useApiMutation<SandboxSession, { sessionId: string }>(
    ({ sessionId }) => ({
      path: `/sandbox/sessions/${sessionId}/reset-webhook`,
      init: { method: 'POST' },
      fallback: 'Failed to reset webhook URL',
    }),
  );
}
